<script setup lang="ts">
import {onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {notificationApi} from '@/container';
import BaseContainer from '@/core/components/base/BaseContainer.vue';
import {useSystemStore} from "@/stores/system.store";
import Customer from "@/customer/interfaces/Customer";
import OrderActionModal from "@/order/components/OrderActionModal.vue";
import {DatabaseNotification} from "@/notification/interfaces/DatabaseNotification";
import {TransformerPaginatedData} from "@/core/interfaces/api";
import DatabaseNotificationHelper from "@/notification/service/DatabaseNotificationHelper";

const route = useRoute();
const router = useRouter();
const systemStore = useSystemStore();

const page = ref(1);
const showOrderModal = ref(false);
const orderId = ref<number | null>(null);
const pagination = ref<TransformerPaginatedData['meta']['pagination']>();
const notifications = ref<DatabaseNotification[]>([]);

/**
 * Set the order to display and open the order modal.
 *
 * @param value - The order id.
 */
const setOrder = (value: number) => {
    orderId.value = value;
    showOrderModal.value = true;
};

/**
 * Close the order modal.
 */
const closeModal = () => {
    orderId.value = null;
    showOrderModal.value = false;
};

/**
 * Redirect to the notification resource, or open the order modal
 * or the information modal when there is no route to redirect to.
 *
 * @param notification - The clicked notification.
 */
const openNotification = (notification: DatabaseNotification) => {
    if (notification.type === 'orderCreated') {
        setOrder(notification.data.resource_id);
    }

    const notificationRoute = DatabaseNotificationHelper.generateRoute(notification);

    if (notificationRoute) {
        router.push(notificationRoute);
    }
};

/**
 * Redirect to the customer home page.
 *
 * @param customer - The customer to display.
 */
const showPatient = (customer: Customer) => {
    router.push({
        name: 'customer.home',

        params: {
            customerId: customer.id
        }
    });
};

watch(page, (value) => {
    notificationApi().get(value)
        .then(response => {
            notifications.value = response.data;
            pagination.value = response.meta.pagination;
        });
}, {immediate: true});

watch(() => route.query.orderId, (value) => {
    if (value) {
        setOrder(parseInt(String(value)));
    }
}, {immediate: true});

onMounted(() => {
    notificationApi().markAll()
        .then(() => {
            systemStore.$patch({notificationCount: 0});
        });
});
</script>

<template>
  <BaseContainer>
    <div v-if="notifications.length">
      <ul class="mx-5 py-4 space-y-4">
        <li
          v-for="notification in notifications"
          :key="notification.id"
          class="px-4 py-6 shadow sm:rounded-lg sm:px-6 cursor-pointer"
          :class="[ notification.read_at
            ? 'bg-white hover:bg-gray-200'
            : 'bg-blue-100 hover:bg-blue-200'
          ]"
          @click="openNotification(notification)"
        >
          <div class="sm:flex sm:justify-between sm:items-baseline">
            <h3 class="text-gray-900">
              {{ notification.data.title }}
            </h3>

            <p class="mt-1 text-sm text-gray-600 whitespace-nowrap sm:mt-0 sm:ml-3">
              <time datetime="2021-01-28T19:24">
                {{ notification.formatted.created_at }}
              </time>
            </p>
          </div>

          <div v-if="'description' in notification.data" class="mt-4 space-y-6 text-sm text-gray-800">
            <p>
              {{ notification.data.description }}
            </p>
          </div>
        </li>
      </ul>

      <v-pagination
        v-if="pagination"
        v-model="page"
        :length="pagination.total_pages"
        :total-visible="7"
        circle
      />
    </div>

    <div
      v-else
      class="text-center"
    >
      <svg
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        class="mx-auto h-12 w-12 text-gray-400"
      >
        <path
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>

      <h3 class="mt-2 text-sm font-medium text-gray-900">
        Vous n’avez pas encore reçu de notifications
      </h3>

      <div class="mt-6">
        <button
          type="button"
          class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          @click="$router.back()"
        >
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            class="-ml-1 mr-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z"
            />
          </svg>

          Retour
        </button>
      </div>
    </div>

    <OrderActionModal
      v-if="orderId"
      :opened="showOrderModal"
      :order-id="orderId"
      @close-modal="closeModal"
      @show="showPatient"
    />
  </BaseContainer>
</template>
